import { useState, useEffect } from "react";
import axios from "axios";
import { 
  TrendingUp, 
  Target, 
  AlertTriangle,
  CheckCircle2,
  ArrowUp,
  ArrowDown,
  Calendar,
  BarChart3
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Statistik = () => {
  const [breakEven, setBreakEven] = useState(null);
  const [monatsuebersicht, setMonatsuebersicht] = useState([]);
  const [prognose, setPrognose] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [breakEvenRes, monatRes, prognoseRes] = await Promise.all([
        axios.get(`${API}/statistik/break-even`, { withCredentials: true }),
        axios.get(`${API}/statistik/monatsuebersicht`, { withCredentials: true }),
        axios.get(`${API}/statistik/prognose`, { withCredentials: true })
      ]);
      setBreakEven(breakEvenRes.data);
      setMonatsuebersicht(monatRes.data);
      setPrognose(prognoseRes.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatMonat = (monatStr) => {
    const [year, month] = monatStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-stone-200 p-3 shadow-lg">
          <p className="font-body text-sm font-medium mb-2">{label}</p>
          {payload.map((p, idx) => (
            <p key={idx} className="font-mono text-sm" style={{ color: p.color }}>
              {p.name}: {formatCurrency(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = [...monatsuebersicht].reverse().map(m => ({
    name: formatMonat(m.monat),
    Speisen: m.einnahmen_speisen,
    Getränke: m.einnahmen_getraenke,
    Sonstiges: m.einnahmen_sonstiges,
    Gesamt: m.gesamt
  }));

  const wochentagData = prognose?.prognose_verfuegbar 
    ? prognose.wochentag_analyse.map(w => ({
        name: w.tag.slice(0, 2),
        Umsatz: w.durchschnitt
      }))
    : [];

  return (
    <div data-testid="statistik-page">
      {/* Header */}
      <header className="page-header">
        <h1 className="page-title">Statistik & Prognose</h1>
        <p className="page-subtitle">Umsatzanalyse und Break-Even Berechnung</p>
      </header>

      {/* Break-Even Section */}
      {breakEven && (
        <div className="px-8 py-4">
          <h2 className="font-display text-lg font-semibold mb-4 text-[#3A4D39]">Break-Even Analyse</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Break-Even Card */}
            <div className="bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#FFF3E0] text-[#D96C4A]">
                  <Target size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#858585]">Break-Even Punkt</p>
                  <p className="font-mono text-2xl font-medium text-[#1A1A1A]">
                    {formatCurrency(breakEven.break_even.pro_monat)}
                  </p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5C5C5C]">Pro Tag (26 Tage):</span>
                  <span className="font-mono">{formatCurrency(breakEven.break_even.pro_tag)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5C5C5C]">Pro Woche:</span>
                  <span className="font-mono">{formatCurrency(breakEven.break_even.pro_woche)}</span>
                </div>
              </div>
            </div>

            {/* Current Month Progress */}
            <div className="bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 ${breakEven.status === 'gewinn' ? 'bg-[#E8F5E9] text-[#2E7D32]' : 'bg-[#FFF3E0] text-[#D96C4A]'}`}>
                  {breakEven.status === 'gewinn' ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#858585]">Aktueller Monat</p>
                  <p className="font-mono text-2xl font-medium text-[#1A1A1A]">
                    {formatCurrency(breakEven.aktueller_monat.umsatz_bisher)}
                  </p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#5C5C5C]">Fortschritt</span>
                  <span className={`font-mono ${breakEven.aktueller_monat.fortschritt_prozent >= 100 ? 'text-[#2E7D32]' : 'text-[#D96C4A]'}`}>
                    {breakEven.aktueller_monat.fortschritt_prozent}%
                  </span>
                </div>
                <div className="w-full bg-stone-200 h-2">
                  <div 
                    className={`h-full transition-all ${breakEven.aktueller_monat.fortschritt_prozent >= 100 ? 'bg-[#2E7D32]' : 'bg-[#D96C4A]'}`}
                    style={{ width: `${Math.min(breakEven.aktueller_monat.fortschritt_prozent, 100)}%` }}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                {breakEven.aktueller_monat.differenz_zu_break_even >= 0 ? (
                  <>
                    <ArrowUp size={14} className="text-[#2E7D32]" />
                    <span className="text-[#2E7D32] font-mono">
                      +{formatCurrency(breakEven.aktueller_monat.differenz_zu_break_even)}
                    </span>
                    <span className="text-[#858585]">über Break-Even</span>
                  </>
                ) : (
                  <>
                    <ArrowDown size={14} className="text-[#C62828]" />
                    <span className="text-[#C62828] font-mono">
                      {formatCurrency(Math.abs(breakEven.aktueller_monat.differenz_zu_break_even))}
                    </span>
                    <span className="text-[#858585]">fehlen noch</span>
                  </>
                )}
              </div>
            </div>

            {/* Costs Overview */}
            <div className="bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-[#FFEBEE] text-[#C62828]">
                  <BarChart3 size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#858585]">Monatliche Kosten</p>
                  <p className="font-mono text-2xl font-medium text-[#1A1A1A]">
                    {formatCurrency(breakEven.kosten.gesamt_monatlich)}
                  </p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5C5C5C]">Personalkosten:</span>
                  <span className="font-mono">{formatCurrency(breakEven.kosten.personal_monatlich)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5C5C5C]">Fixkosten:</span>
                  <span className="font-mono">{formatCurrency(breakEven.kosten.fixkosten_monatlich)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-stone-100">
                  <span className="text-[#5C5C5C]">Ø Marge:</span>
                  <span className="font-mono text-[#3A4D39]">{breakEven.margen.gewichtet_gesamt}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Prognose */}
          {breakEven.aktueller_monat.tage_erfasst > 0 && (
            <div className="bg-[#E8F5E9] border border-[#C8E6C9] p-4 mb-6">
              <div className="flex items-start gap-3">
                <TrendingUp size={20} className="text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#2E7D32] text-sm">Monatsprognose</p>
                  <p className="text-sm text-[#1B5E20] mt-1">
                    Basierend auf {breakEven.aktueller_monat.tage_erfasst} erfassten Tagen: 
                    <span className="font-mono font-medium ml-1">
                      {formatCurrency(breakEven.aktueller_monat.prognose_monat)}
                    </span>
                    {breakEven.aktueller_monat.prognose_monat >= breakEven.break_even.pro_monat
                      ? " - Sie sind auf gutem Weg!"
                      : ` - noch ${formatCurrency(breakEven.break_even.pro_monat - breakEven.aktueller_monat.prognose_monat)} zum Break-Even`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Monthly Overview Chart */}
      {chartData.length > 0 && (
        <div className="px-8 py-4">
          <h2 className="font-display text-lg font-semibold mb-4 text-[#3A4D39]">Monatsumsätze</h2>
          <div className="bg-white border border-stone-200 p-5">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="Speisen" fill="#3A4D39" stackId="a" />
                <Bar dataKey="Getränke" fill="#5C7A5A" stackId="a" />
                <Bar dataKey="Sonstiges" fill="#8BA888" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Weekday Analysis */}
      {prognose?.prognose_verfuegbar && (
        <div className="px-8 py-4 pb-8">
          <h2 className="font-display text-lg font-semibold mb-4 text-[#3A4D39]">Wochentag-Analyse</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chart */}
            <div className="bg-white border border-stone-200 p-5">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={wochentagData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Umsatz" fill="#3A4D39" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Insights */}
            <div className="space-y-4">
              {/* Best Days */}
              <div className="bg-white border border-stone-200 p-4">
                <p className="text-xs uppercase tracking-wide text-[#858585] mb-3">Beste Tage</p>
                <div className="space-y-2">
                  {prognose.beste_tage.slice(0, 3).map((tag, idx) => (
                    <div key={tag.tag} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          idx === 0 ? 'bg-[#FFD700] text-[#1A1A1A]' : 
                          idx === 1 ? 'bg-[#C0C0C0] text-[#1A1A1A]' : 
                          'bg-[#CD7F32] text-white'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="font-medium">{tag.tag}</span>
                      </div>
                      <span className="font-mono text-[#3A4D39]">{formatCurrency(tag.durchschnitt)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prognosis Summary */}
              <div className="bg-white border border-stone-200 p-4">
                <p className="text-xs uppercase tracking-wide text-[#858585] mb-3">Zu erwartende Umsätze</p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#5C5C5C]">Pro Woche:</span>
                    <span className="font-mono text-lg font-medium">{formatCurrency(prognose.prognosen.pro_woche)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#5C5C5C]">Pro Monat:</span>
                    <span className="font-mono text-lg font-medium">{formatCurrency(prognose.prognosen.pro_monat)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-stone-100">
                    <span className="text-[#5C5C5C]">Pro Jahr:</span>
                    <span className="font-mono text-xl font-medium text-[#3A4D39]">{formatCurrency(prognose.prognosen.pro_jahr)}</span>
                  </div>
                </div>
                <p className="text-xs text-[#858585] mt-3">
                  Basierend auf {prognose.datenbasis.tage_gesamt} erfassten Tagen
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {monatsuebersicht.length === 0 && (
        <div className="px-8 py-4">
          <div className="bg-[#FFF3E0] border border-[#FFE0B2] p-4">
            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-[#E65100] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#E65100] text-sm">Noch keine Umsatzdaten</p>
                <p className="text-sm text-[#BF360C] mt-1">
                  Erfassen Sie Ihre täglichen Einnahmen unter "Einnahmen", um Statistiken und Prognosen zu sehen.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistik;
